import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, useTheme, Divider, Tooltip, Avatar } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";
import MiniBot from '../../MiniBot';
import { useAuth } from '../../../helpers/AuthProvider';
import queryGraphql from '../../../helpers/ApiInterface'; // Import the helper

// --- Interfaces & Data ---
// Interface updated to match the shape our component expects. Properties will be mapped from the API.
interface GatewayData {
    id: string;
    name: string;
    apiType: string; // Use string to accept API data
    endpointType: string; // Use string
    userPoolName?: string; // Made optional as it's not in the new query
    region: string;
    wafEnabled: boolean;
    tokenHeaderSource: string;
    status: string; // Use string
    solutionOwner: string;
    createdAt: string;
    invokeUrl?: string; // Made optional
    reasonForRejection?: string;
}

// Hardcoded data removed

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | null | boolean }> = ({ label, value }) => {
    let displayValue: string;
    if (typeof value === 'boolean') {
        displayValue = value ? 'Yes' : 'No';
    } else {
        displayValue = value || '-';
    }
    
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{displayValue}</Typography>
        </Box>
    );
};

const getResourceStatusesQuery = `
  query ResourceStatuses {
    resourceStatuses {
      resourceName
      projectUrl
    }
  }
`;

// --- Main Content Component ---
const APIGatewayDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [gateway, setGateway] = useState<GatewayData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const [invokeUrl, setInvokeUrl] = useState<string | null>(null); // State for the separately fetched invoke URL
    const [apiGatewayRepo, setApiGatewayRepo] = useState<string| null>(null);
    useEffect(() => {
        const fetchGatewayData = async () => {
            if (!id) {
                setError("Gateway ID is missing from URL.");
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                // Fetch using the provided GraphQL API
                const query = `
                    query GetAPIGateway($getApiGatewayId: String!) {
                      getAPIGateway(id: $getApiGatewayId) {
                        id
                        name
                        apiGatewayType
                        apiEndpointType
                        region
                        authorizerType
                        tokenHeaderSource
                        awsWafEnabled
                        status
                        reasonForRejection
                        userPoolName
                        createdAt
                        apiGatewayOwner {
                          id
                          name
                        }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getApiGatewayId: id });
                
                if (response.getAPIGateway) {
                    const apiData = response.getAPIGateway;

                    // Transform API data to the shape our component expects (GatewayData interface)
                    const transformedGateway: GatewayData = {
                        id: apiData.id,
                        name: apiData.name,
                        apiType: apiData.apiGatewayType,
                        endpointType: apiData.apiEndpointType,
                        region: apiData.region,
                        wafEnabled: apiData.awsWafEnabled,
                        tokenHeaderSource: apiData.tokenHeaderSource,
                        status: apiData.status.toLowerCase(),
                        solutionOwner: apiData.apiGatewayOwner?.name || 'N/A',
                        createdAt: new Date(Number(apiData.createdAt)).toISOString(),
                        reasonForRejection: apiData.reasonForRejection,
                        userPoolName: apiData.userPoolName,
                        // userPoolName and invokeUrl are not in this query and will be fetched separately or omitted
                    };
                    setGateway(transformedGateway);
                } else {
                    setError("API Gateway not found or an error occurred.");
                }
            } catch (err: any) {
                setError(err.message || "Failed to fetch Gateway details.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchGatewayData();
    }, [id]);

    // Secondary effect to fetch the Invoke URL ONLY IF the gateway is active
    useEffect(() => {
        const fetchInvokeUrl = async () => {
            if (!gateway || gateway.status !== 'active' || !gateway.id) {
                return; // Don't fetch if no gateway, or if it's not active
            }
            try {
                // This query fetches the endpoint URL from resourceStatus, matching logic from other detail pages
                const data = await queryGraphql(
                    `query Query($resourceStatusId: String!) {
                      resourceStatus(id: $resourceStatusId) {
                          solutionEndpointUrl
                          projectUrl
                      }
                    }`, 
                    { resourceStatusId: gateway.id }
                );
                if (data.resourceStatus?.solutionEndpointUrl) {
                    setInvokeUrl(data.resourceStatus.solutionEndpointUrl);
                    setApiGatewayRepo(data.resourceStatus.projectUrl);
                }
            } catch (error) {
                console.error("Error fetching invoke URL:", error);
            }
        };

        fetchInvokeUrl();
    }, [gateway]); // This effect runs whenever the 'gateway' object changes

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>Loading Gateway details...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{ p: 2 }}>{error}</Typography>;
    }

    if (!gateway) {
        return <Typography sx={{ p: 2 }}>No API Gateway data available.</Typography>;
    }

    const getStatusBackgroundColor = (status: string): string => {
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active") return "#12B76A10";
        if (lowerStatus === "rejected") return "#F0443810";
        if (lowerStatus === "pending") return "#FDB02210";
        return "#F8F8F8";
    };

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/apis-gateway" onClick={(e) => { e.preventDefault(); navigate('/apis-gateway'); }}>API Gateways</Link>
                <Typography color="text.primary" sx={{ fontSize: "14px" }}>Gateway Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/apis-gateway')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold', fontSize: "18px" }}>API Gateway Details</Typography>
            </Box>

            <Box sx={{ boxShadow: "0px 3px 6px #10101029", borderRadius: "8px", bgcolor: '#fff' }}>
                <Box sx={{ p: 2.5, bgcolor: getStatusBackgroundColor(gateway.status), borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                            {gateway.name}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                        Resource Owner: {gateway.solutionOwner}
                    </Typography>
                    {user.roles?.some((e: string) => ["developer"].includes(e)) && gateway.status =="pending" && (
                                                <IconButton sx={{ p: 0.5 }} onClick={() => navigate(`/apis-gateway/edit/${id}`)}>
                                                    <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                                                </IconButton>
                                            )}
                                            </Box>
                </Box>

                <Grid container spacing={3} sx={{ p: 3, pt: 2.5 }}>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="API Type" value={gateway.apiType} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Endpoint Type" value={gateway.endpointType} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="AWS Region" value={gateway.region} /></Grid>
                    {/* This field is not in the new API and will correctly display '-' via the DetailItem component */}
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="User Pool Name" value={gateway.userPoolName} /></Grid>
                </Grid>

                <Box sx={{ mt: 0, p: 2.5, borderTop: "1px solid #e0e0e0" }}>
                    <Typography component="h3" sx={{ fontFamily: 'Gilroy-Medium', fontSize: '14px', pb: 1 }}>Configuration Details</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={3}><DetailItem label="AWS WAF Enabled" value={gateway.wafEnabled} /></Grid>
                        <Grid item xs={6} sm={3}><DetailItem label="Token Header Source" value={gateway.tokenHeaderSource} /></Grid>
                        
                        {/* --- UPDATED INVOKE URL ITEM --- */}
                        {/* This block now reads from the 'invokeUrl' state variable and only renders if it exists (i.e., status is active and fetch was successful) */}
                        {invokeUrl && (
                            <Grid item xs={6} sm={3}>
                                <Typography 
                                    variant="caption" 
                                    display="block" 
                                    sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}
                                >
                                    API Gateway Endpoint
                                </Typography>
                                <Tooltip
                                    title={
                                        <>
                                            <Typography 
                                                fontFamily="Gilroy-Bold" 
                                                fontSize="14px"
                                            >
                                                API Gateway Endpoint
                                            </Typography>
                                            
                                            {/* Conditionally render Link vs. plain text */}
                                            {invokeUrl && invokeUrl !== "Loading..." ? (
                                                <Link
                                                    href={invokeUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        color: '#ffffff',
                                                        textDecoration: 'underline',
                                                        wordBreak: 'break-all' // Ensures long URLs wrap
                                                    }}
                                                >
                                                    Gateway URL
                                                </Link>
                                            ) : (
                                                <Typography 
                                                    fontFamily="Gilroy-Regular" 
                                                    fontSize="12px" 
                                                    sx={{ color: '#ffffff' }}
                                                >
                                                    Loading...
                                                </Typography>
                                            )}
                                        </>
                                    }
                                    slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
                                >
                                    <IconButton sx={{ p: 0 }}>
                                        <Avatar
                                            src="/assets/info.svg"
                                            alt="info"
                                            sx={{ height: 16, width: 16, cursor: 'pointer' }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        )}

                        {apiGatewayRepo && (
                            <Grid item xs={6} sm={3}>
                                <Typography 
                                    variant="caption" 
                                    display="block" 
                                    sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}
                                >
                                    API Gateway Repo
                                </Typography>
                                <Tooltip
                                    title={
                                        <>
                                            <Typography 
                                                fontFamily="Gilroy-Bold" 
                                                fontSize="14px"
                                            >
                                                API Gateway Repo
                                            </Typography>
                                            
                                            {/* Conditionally render Link vs. plain text */}
                                            {apiGatewayRepo && apiGatewayRepo !== "Loading..." ? (
                                                <Link
                                                    href={apiGatewayRepo}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        color: '#ffffff',
                                                        textDecoration: 'underline',
                                                        wordBreak: 'break-all' // Ensures long URLs wrap
                                                    }}
                                                >
                                                    Gateway Repo
                                                </Link>
                                            ) : (
                                                <Typography 
                                                    fontFamily="Gilroy-Regular" 
                                                    fontSize="12px" 
                                                    sx={{ color: '#ffffff' }}
                                                >
                                                    Loading...
                                                </Typography>
                                            )}
                                        </>
                                    }
                                    slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
                                >
                                    <IconButton sx={{ p: 0 }}>
                                        <Avatar
                                            src="/assets/info.svg"
                                            alt="info"
                                            sx={{ height: 16, width: 16, cursor: 'pointer' }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        )}                        {/* --- END OF INVOKE URL ITEM --- */}

                    </Grid>
                </Box>
            </Box>
            
            {gateway.status === 'rejected' && (
                <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                    <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                    <Divider sx={{ marginY: 2 }} />
                    <DetailItem label="Reason for Rejection" value={gateway.reasonForRejection} />
                </Box>
            )}
        </Container>
    );
};

// --- Main Page Component ---
const APIGatewayDetailsPage: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        return typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);
    const handleDrawerStateChange = (isOpen: boolean) => setIsDrawerActuallyOpen(isOpen);
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"apis-gateway"} onStateChange={handleDrawerStateChange} />
            <MiniBot />
            <Box component="main" sx={{
                flexGrow: 1,
                p: 0,
                transition: theme.transitions.create('margin', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                marginLeft: { xs: 0, sm: `${currentMarginLeft}px` },
            }}>
                <APIGatewayDetailsPageContent />
            </Box>
        </Box>
    );
};

export default APIGatewayDetailsPage;